var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { getCalendarVisits } from '../services/CalendarService';
import { calendarKeys } from './queryKeys';
const STALE_TIME = 2 * 60 * 1000;
function buildAdjacentRange(base, direction) {
    // Slice to YYYY-MM-DD for arithmetic to avoid any DST drift in day diff/add.
    const startDatePart = base.startStr.slice(0, 10);
    const endDatePart = base.endStr.slice(0, 10);
    const durationDays = dayjs(endDatePart).diff(dayjs(startDatePart), 'day');
    const newStartDatePart = dayjs(startDatePart)
        .add(direction * durationDays, 'day')
        .format('YYYY-MM-DD');
    const newEndDatePart = dayjs(endDatePart)
        .add(direction * durationDays, 'day')
        .format('YYYY-MM-DD');
    // Preserve the time+timezone suffix (e.g. "T00:00:00-05:00") from the base range so the
    // adjacent range sends timezone-aware strings to the API — same as the primary fetch fix.
    const startSuffix = base.startStr.length > 10 ? base.startStr.slice(10) : '';
    const endSuffix = base.endStr.length > 10 ? base.endStr.slice(10) : '';
    const newStartStr = newStartDatePart + startSuffix;
    const newEndStr = newEndDatePart + endSuffix;
    return {
        start: new Date(newStartStr),
        end: new Date(newEndStr),
        startStr: newStartStr,
        endStr: newEndStr,
        view: base.view
    };
}
export function usePrefetchCalendar({ range, filters, selectedTechnicianIds, selectedSubcontractorIds, view, enabled }) {
    const queryClient = useQueryClient();
    useEffect(() => {
        if (!(range === null || range === void 0 ? void 0 : range.startStr) || !enabled)
            return;
        const adjacentRanges = [buildAdjacentRange(range, -1), buildAdjacentRange(range, +1)];
        adjacentRanges.forEach((adjRange) => {
            void queryClient.prefetchQuery({
                queryKey: calendarKeys.visits(adjRange.startStr, adjRange.endStr, view, filters, selectedTechnicianIds, selectedSubcontractorIds),
                queryFn: (_a) => __awaiter(this, [_a], void 0, function* ({ signal }) {
                    const response = yield getCalendarVisits({
                        range: adjRange,
                        filters,
                        selectedTechnicianIds,
                        selectedSubcontractorIds,
                        view
                    }, signal);
                    if (response.error) {
                        throw new Error(response.error.message);
                    }
                    return response.data;
                }),
                staleTime: STALE_TIME
            });
        });
    }, [
        enabled,
        filters,
        queryClient,
        range === null || range === void 0 ? void 0 : range.endStr,
        range === null || range === void 0 ? void 0 : range.startStr,
        selectedSubcontractorIds,
        selectedTechnicianIds,
        view
    ]);
}
